/**
 * What the shift cost, as a receipt - React edition of ui/Debrief.ts.
 *
 * The two numbers are the same everywhere because they are the two a real fab
 * is judged on - tool availability and product that will not ship. Read
 * src/learn/shift.ts for why there is no par and no combined score.
 */

import type { Journal } from '../../learn/Journal'
import { callsMade, currentShiftEvents, sessionTally, shiftCost } from '../../learn/shift'
import { t } from '../../i18n/i18n'
import { hoursText } from '../Debrief'
import { useLang } from '../../app/i18n/LanguageProvider'
import { useTick } from '../../app/state/useObservable'
import { StatTile } from './primitives'
import { CallsMade } from './CallsMade'
import type { Readout } from '../../assistant/types'

/**
 * Hand the instructor the session record. Opened in a window rather than
 * downloaded: a student on a lab machine often cannot write to disk, and a
 * page can always be read, copied or printed.
 */
function exportRecord(journal: Journal): void {
  const text = journal.export()
  const w = window.open('', '_blank')
  if (!w) {
    console.log(text)
    return
  }
  w.document.title = t('tool.common.SessionRecord')
  const pre = w.document.createElement('pre')
  pre.style.cssText = 'white-space:pre-wrap;font:13px/1.5 system-ui;padding:24px'
  pre.textContent = text
  w.document.body.appendChild(pre)
}

/**
 * The receipt itself - static per render; wrap it in DebriefSection for one
 * that follows the journal.
 */
export function DebriefBlock({
  journal,
  tool,
  heading = 'shift.Debrief',
}: {
  journal: Journal
  tool?: string
  heading?: string
}) {
  const events = currentShiftEvents(journal, tool)
  const cost = shiftCost(events)
  const calls = callsMade(events)
  const tally = sessionTally(journal)
  const hrs = (h: number) => t('shift.Hours', { hours: hoursText(h) })

  // Which KPIs this tool has at all: a tool with no fault model has no repair
  // to order (downtime structurally zero, and a zero would read as a good
  // score), and a tool that has produced nothing has no spec verdict.
  const hasDowntime = cost.totalHours > 0 || cost.correct !== null
  const hasRuns = cost.runs > 0

  const tiles: Readout[] = []
  if (hasDowntime) {
    tiles.push({
      label: t('shift.KpiDowntime'),
      value: hrs(cost.totalHours),
      note: t('shift.KpiDowntimeNote'),
      state: cost.correct === false ? 'warning' : undefined,
    })
  }
  if (hasRuns) {
    tiles.push({
      label: t('shift.KpiOutOfSpec'),
      value: String(cost.outOfSpec),
      note: t('shift.KpiOutOfSpecNote', { runs: cost.runs }),
      state: cost.outOfSpec > 0 ? 'warning' : undefined,
    })
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div className="blk">
        <div className="k">{t(heading)}</div>
      </div>
      {tiles.length > 0 ? (
        <div className="grid-2" style={{ marginTop: 7 }}>
          {tiles.map((r, i) => (
            <StatTile key={i} r={r} />
          ))}
        </div>
      ) : (
        <div className="session-tally">{t('shift.NothingYet')}</div>
      )}

      {/* Itemised, because the total is not the lesson - the relative sizes
          are. A one-hour reading next to an eight-hour wrong repair argues
          for taking the reading better than any wording could. */}
      {cost.lines.length > 0 && (
        <div className="cost-lines">
          {cost.lines.map((line, i) => (
            <div className="cost-line" key={i}>
              <span>{line.label}</span>
              <b>{hrs(line.hours)}</b>
            </div>
          ))}
          <div className="cost-line total">
            <span>{t('shift.CostTotal')}</span>
            <b>{hrs(cost.totalHours)}</b>
          </div>
        </div>
      )}

      <CallsMade calls={calls} />

      {/* The only standings the project should have: the student against
          their own afternoon. */}
      {tally.shifts > 1 ? (
        <div className="session-tally">
          {t('shift.SessionTally', {
            shifts: tally.shifts,
            correct: tally.correct,
            called: tally.called,
            hours: hoursText(tally.totalHours),
            scrapped: tally.totalOutOfSpec,
          })}
        </div>
      ) : tally.tools > 1 ? (
        <div className="session-tally">
          {t('shift.SessionTallyRuns', {
            runs: tally.totalRuns,
            tools: tally.tools,
            scrapped: tally.totalOutOfSpec,
          })}
        </div>
      ) : null}

      <div className="btn-row" style={{ marginTop: 12 }}>
        <button className="btn" onClick={() => exportRecord(journal)}>
          {t('tool.common.ExportRecord')}
        </button>
      </div>
    </div>
  )
}

/** The performance block that keeps itself current - follows the journal. */
export function DebriefSection({
  journal,
  tool,
  heading = 'shift.Performance',
}: {
  journal: Journal
  tool: string
  heading?: string
}) {
  useLang()
  useTick((fn) => journal.subscribe(fn))
  return <DebriefBlock journal={journal} tool={tool} heading={heading} />
}
